import { useEffect, useContext, useState } from 'react'
import { useProjectStore } from 'Store/Project'
import { useSaleOrderStore } from 'Store/SaleOrder'
import { useAuthStore } from 'Store/Auth'
import { useAppStore } from 'Store/App'
import { useNavigate } from 'react-router-dom'
import { ProjectListViewContext } from './Context'
import { TableContext } from 'Context/Table'

import StatusCell from './Components/StatusCell'
import ActionCell from 'Presentation/Components/Table/Components/CustomActionCell'

import formatNumberCommas from 'Utils/formatNumberCommas'
import Swal from 'sweetalert2'
import { toast } from 'react-toastify'
import { shallow } from 'zustand/shallow'
import moment from 'moment'

const INITIAL_FILTER = {
  page: 1,
  pageSize: 20,
  saleOrderId: '',
  division: '',
  projectStatus: '',
}

const ViewModel = () => {
  const navigate = useNavigate()
  const { data, totalData, getAllPagination, deleteProject } = useProjectStore(
    state => ({
      data: state.data,
      totalData: state.totalData,
      getAllPagination: state.getAllPagination,
      deleteProject: state.delete,
    }),
    shallow
  )
  const { saleOrder, getAllSaleOrder } = useSaleOrderStore(
    state => ({
      saleOrder: state.data,
      getAllSaleOrder: state.getAll,
    }),
    shallow
  )
  const { profile } = useAuthStore(state => ({ profile: state.profile }), shallow)
  const { isOpenImportProjectModal, toggleImportProjectModal, toggleExportDailyReportModal } =
    useContext(ProjectListViewContext)
  const {
    currentSorting,
    updateLoading,
    updateError,
    updatePagination,
    updateSorting,
    resetState: resetTableState,
  } = useContext(TableContext)
  const [filter, setFilter] = useState(INITIAL_FILTER)
  const [isTableReady, setIsTableReady] = useState(false)

  const setShowBackButton = useAppStore(state => state.setShowBackButton, shallow)

  const userRoleId = profile?.roleId

  const isLogistic = [21].includes(userRoleId)
  const isSale = [7].includes(userRoleId)
  const isCheif = [2].includes(userRoleId)

  const onChangeFilter = (name: string, value: any) => {
    setFilter(prev => ({ ...prev, [name]: value }))
  }

  const nextPageDesktop = async (page: number) => {
    const nextFilter = {
      ...filter,
      page,
    }
    setFilter(nextFilter)
    await fetchData(nextFilter)
  }

  const submitFilter = async () => {
    const nextFilter = {
      ...filter,
      page: 1,
    }
    setFilter(nextFilter)
    await fetchData(nextFilter)
  }

  const clearFilter = async () => {
    setFilter(INITIAL_FILTER)
    await fetchData(INITIAL_FILTER)
  }

  const handleOpenImportProjectModal = () => {
    toggleImportProjectModal(true)
  }

  const handleOpenExportDailyReportModal = () => {
    toggleExportDailyReportModal(true)
  }

  const handleCloseImportProjectModal = () => {
    toggleImportProjectModal(false)
  }

  const setupTable = () => {
    updateSorting('id', true)
    updatePagination(true)
  }

  const fetchMasterData = () => {
    getAllSaleOrder()
  }

  const fetchData = async (nextFilter?: typeof INITIAL_FILTER) => {
    const currentSort = currentSorting?.[0]
    const currentFilter: any = {
      ...(nextFilter ?? filter),
      sortBy: currentSort?.id ?? 'id',
      order: currentSort?.desc ? 'desc' : 'asc',
    }

    try {
      updateLoading(true)
      const { success, data: responseData } = await getAllPagination(currentFilter)
      if (!success) {
        updateError(true)
      } else {
        onChangeFilter('page', responseData?.page ?? currentFilter.page)
        updateError(false)
      }

      updateLoading(false)
    } catch (err) {
      updateLoading(false)
      updateError(true)
    }
  }

  const viewProject = (id: number) => {
    navigate(`/project/${id}/overview`)
  }

  const askDeleteProject = async (id: string) => {
    const { isConfirmed } = await Swal.fire({
      title: 'คุณต้องการที่จะลบโครงการนี้ใช่หรือไม่?',
      text: 'โครงการที่ถูกลบไปแล้วจะไม่สามารถกู้คืนได้',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'ยืนยัน',
      cancelButtonText: 'ยกเลิก',
    })
    if (isConfirmed) {
      const { success, data } = await deleteProject(parseInt(id))
      if (success) {
        toast.success('ลบโครงการสำเร็จ')
        fetchData()
      } else {
        toast.error(data)
      }
    }
  }

  const TABLE_CONFIGS: any[] = [
    {
      Header: 'ลำดับ',
      accessor: 'id',
      width: 50,
      minWidth: 50,
      Cell: ({ value }: any) => {
        return <span className='text-gray-800'>{value}</span>
      },
    },
    {
      Header: 'ชื่อโครงการ',
      accessor: 'division',
      width: 200,
      minWidth: 200,
      Cell: ({ value }: any) => {
        return <span className='text-gray-800'>{value}</span>
      },
    },
    {
      Header: 'สถานะ',
      accessor: 'projectState',
      width: 100,
      minWidth: 100,
      Cell: ({ value }: any) => {
        return <StatusCell status={value} />
      },
    },
    {
      Header: 'วันที่เริ่มโครงการ',
      accessor: 'startJobDate',
      width: 100,
      minWidth: 100,
      Cell: ({ value }: any) => {
        return (
          <span className='text-gray-800'>
            {moment(value).isValid() ? moment(value).format('DD/MM/YYYY') : '-'}
          </span>
        )
      },
    },
    {
      Header: 'วันที่โครงการเสร็จสิ้น',
      accessor: 'endJobDate',
      width: 120,
      minWidth: 120,
      Cell: ({ value }: any) => {
        return (
          <span className='text-gray-800'>
            {moment(value).isValid() ? moment(value).format('DD/MM/YYYY') : '-'}
          </span>
        )
      },
    },
    {
      Header: 'จำนวนเสา (ท่อน)',
      headerRight: true,
      accessor: 'totalPileAmount',
      width: 150,
      minWidth: 150,
      Cell: ({ value }: any) => {
        return (
          <span className='text-gray-800 d-flex justify-content-end'>
            {formatNumberCommas(value)}
          </span>
        )
      },
    },
    {
      Header: 'ยอดจำนวนที่ตอกไปแล้ว (ต้น)',
      headerRight: true,
      accessor: 'totalHammered',
      width: 150,
      minWidth: 150,
      Cell: ({ value }: any) => {
        return (
          <span className='text-gray-800 d-flex justify-content-end'>
            {formatNumberCommas(value)}
          </span>
        )
      },
    },
    {
      Header: '',
      accessor: 'action',
      width: 100,
      minWidth: 100,
      Cell: ({ row }: any) => (
        <div className='d-flex justify-content-end'>
          <ActionCell
            id={row.original.id}
            hideEdit
            hideDuplicate
            hideDelete={isLogistic || isSale || isCheif}
            onClickView={() => viewProject(row.original.id)}
            onClickDelete={() => askDeleteProject(row.original.id)}
          />
        </div>
      ),
      disableSortBy: true,
    },
  ]

  useEffect(() => {
    fetchMasterData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Initial Table Setup
  useEffect(() => {
    setupTable()
    setShowBackButton(false)
    setIsTableReady(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!isTableReady) {
      return
    }

    fetchData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSorting, isTableReady])

  // Reset Hook
  useEffect(() => {
    return () => {
      resetTableState()
      setShowBackButton(false)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return {
    isOpenImportProjectModal,
    handleOpenImportProjectModal,
    handleOpenExportDailyReportModal,
    handleCloseImportProjectModal,
    data,
    totalData,
    saleOrder,
    TABLE_CONFIGS,
    filter,
    onChangeFilter,
    nextPageDesktop,
    submitFilter,
    clearFilter,
  }
}

export default ViewModel
